import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import StepAudience from "./components/StepAudience";
import StepMessage from "./components/StepMessage";
import StepSchedule from "./components/StepSchedule";
import StepReview from "./components/StepReview";

import { useToast } from "@/components/base/Toast";

const steps = [
  { label: "Audience & Details", key: 1 },
  { label: "Message", key: 2 },
  { label: "Schedule", key: 3 },
  { label: "Review & Launch", key: 4 },
];

export interface CampaignDraftData {
  name: string;
  type: string;
  channel: string;
  targetAudience: string;
  leadCount: number;
  templateName: string;
  sendOption: "now" | "later";
  scheduleDate?: string;
  scheduleTime?: string;
  customText?: string;
}

export default function NewCampaignPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [draft, setDraft] = useState<CampaignDraftData>({
    name: "Site Visit Invitation — June",
    type: "Promotional",
    channel: "WhatsApp",
    targetAudience: "All Sources • All Stages",
    leadCount: 156,
    templateName: "Site Visit Invitation",
    sendOption: "now",
  });

  const progressPercent = ((currentStep - 1) / (steps.length - 1)) * 100;

  const goNext = () => {
    if (currentStep === 1) {
      if (!draft.name || !draft.name.trim()) {
        showToast("Please enter a Campaign Name to proceed", "error");
        return;
      }
    }
    setCurrentStep((prev) => Math.min(prev + 1, steps.length));
  };
  const goBack = () => {
    if (currentStep === 1) navigate("/campaigns");
    else setCurrentStep((prev) => prev - 1);
  };

  const updateDraft = (data: Partial<CampaignDraftData>) => {
    setDraft((prev) => ({ ...prev, ...data }));
  };

  return (
    <CrmLayout title="New Campaign">
      <div className="p-5 md:p-6 max-w-3xl mx-auto space-y-5">
        {/* Back link */}
        <button
          onClick={() => navigate("/campaigns")}
          className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-indigo-600 font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> Back to Campaigns
        </button>

        {/* Step indicator */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            {steps.map((step, idx) => (
              <div key={step.key} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                      step.key < currentStep
                        ? "bg-green-500 text-white"
                        : step.key === currentStep
                        ? "bg-indigo-600 text-white shadow-xs"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {step.key < currentStep ? <i className="ri-check-line" /> : step.key}
                  </div>
                  <span
                    className={`text-xs mt-1.5 font-semibold whitespace-nowrap ${
                      step.key === currentStep ? "text-indigo-600" : step.key < currentStep ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2 mt-[-16px] bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                      style={{ width: step.key < currentStep ? "100%" : "0%" }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Progress bar */}
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        {currentStep === 1 && (
          <StepAudience
            onNext={goNext}
            draft={draft}
            onUpdateDraft={updateDraft}
          />
        )}
        {currentStep === 2 && (
          <StepMessage
            onBack={goBack}
            onNext={goNext}
            draft={draft}
            onUpdateDraft={updateDraft}
          />
        )}
        {currentStep === 3 && (
          <StepSchedule
            onBack={goBack}
            onNext={goNext}
            draft={draft}
            onUpdateDraft={updateDraft}
          />
        )}
        {currentStep === 4 && (
          <StepReview
            onBack={goBack}
            draft={draft}
          />
        )}
      </div>
    </CrmLayout>
  );
}